<script lang="ts">
  import { untrack } from 'svelte';
  import { afterNavigate } from '$app/navigation';

  import Icon from '$shared/atoms/Icon/Icon.svelte';
  import { IconType } from '$shared/constants/IconType';
  import { ModalFakeInputElementId } from '$shared/constants/global';
  import { modalRefreshTick } from '$shared/stores/modalContextStore';
  import { Modal } from '$shared/models/Modal';
  import { ModalPopupHookService } from '$shared/lib/services/ModalPopupHookService';

  interface Props {
    modalList: Modal[];
    children?: import('svelte').Snippet;
    onrefresh?: () => void;
  }

  let { modalList, children, onrefresh }: Props = $props();

  let fakeInputElem: any = $state();

  // Reactive props map: tracks latest props per modal id so Svelte 5 can
  // detect changes even though Modal instances are plain (non-$state) objects.
  let modalPropsMap: Record<string, ObjectType<any>> = $state({});

  $effect(() => {
    // Establish a reactive dependency on modalList, then initialize props for
    // any newly added modals without creating a circular dependency on modalPropsMap.
    const currentModals = modalList;
    untrack(() => {
      for (const modal of currentModals) {
        if (!(modal.id in modalPropsMap)) {
          modalPropsMap[modal.id] = modal.props;
        }
      }
    });
  });

  // Watch the tick signal from modalContextStore. The store increments it when
  // document events fire (app-modal:close, app-modal:refresh) or when
  // updateModalProps/registerModalEvent are called. untrack prevents the
  // dispatchRefresh body from creating additional reactive dependencies.
  $effect(() => {
    $modalRefreshTick;
    untrack(() => dispatchRefresh());
  });

  afterNavigate(() => {
    onRouteChange();
  });

  const dispatchRefresh = (): void => {
    // Sync the latest props from each visible modal into the reactive map.
    // This is what actually causes Svelte 5 to re-render the modal components
    // because modalPropsMap is $state() whereas modal.props is not.
    for (const modal of modalList) {
      if (modal.show) {
        modalPropsMap[modal.id] = modal.props;
      }
    }
    onrefresh?.();
  };

  const onRouteChange = () => {
    // Check any modal have closeOnNavigate false, it should be not to close while navigation
    const hasCloseModal = !modalList.find((modal: Modal) => modal.show && !modal.closeOnNavigate);
    if (!hasCloseModal) {
      modalList
        ?.filter((modal: Modal) => modal.show && modal.closeOnNavigate)
        .forEach((modal: Modal) => modal.close());
      return;
    }
    modalList?.filter((modal: Modal) => modal.show).forEach((modal: Modal) => modal.close());
  };

  const handleMouseDown = (e: MouseEvent, modalObj: Modal): void => {
    if (!modalObj?.isDismissable) return;
    e.target === e.currentTarget && modalObj.close();
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    const modalObj = modalList.filter((modal) => modal.show)?.last();
    if (!modalObj?.isDismissable) {
      return;
    }
    if (modalObj?.show && e.which === 27) {
      modalObj.close();
    }
  };
</script>

<svelte:window onkeydown={handleKeyDown} />

<div class="modals">
  <input bind:this={fakeInputElem} id={ModalFakeInputElementId} class="fake-input" />
  {#each modalList as modal (modal.id)}
    {#if modal.show}
      <div
        class="comp-modal"
        class:show={modal.show}
        onmousedown={(e) => {
          handleMouseDown(e, modal);
        }}
      >
        <div class="modal-container {modal.options.style || ''}">
          {#if modal.isDismissable}
            <div class="close-button">
              <Icon
                name="closeModal"
                value="icon-x {modal.isFormStyle ? 'white' : ''}"
                type={IconType.Button}
                onclick={() => modal.close()}
                fontSize="24px"
              />
            </div>
          {/if}
          {#if modal}
            {@const ActiveModalComponent = modal.component
              ? modal.component
              : ModalPopupHookService.ModalCompMap.get(modal.type)}
            {#if ActiveModalComponent}
              <ActiveModalComponent {...(modalPropsMap[modal.id] ?? modal.props)} onevent={modal.handleComponentEvent} />
            {/if}
          {/if}
        </div>
      </div>
    {/if}
  {/each}
</div>

{@render children?.()}

<style src="./Modal.css">
</style>
